/**
 * A plant's or a location's journal: its notes, newest first, and writing,
 * rewriting and removing them.
 *
 * A plant's notes and a location's live in different tables behind different
 * routes; the *For functions take a subject and ask the right one, so the
 * journal on a card does not need to know which it is showing.
 *
 * Endpoints:
 * - A plant's: GET /notes/:plantObjId → Note[] (a bare list); POST
 *   /notes/:plantObjId { content } → [{ newNoteId }] (the procedure's
 *   one-row set); PUT /notes/:noteId { plantObjId, content }; DELETE
 *   /notes/:plantObjId/:noteId; PATCH /notes/:plantObjId/:noteId/public
 *   { hidden } → { success, note_id, hidden_from_public } (a saved Plant Doctor
 *   checkup, hidden from the public pages or shown; the owner only).
 * - A location's: GET /locations/:id/journal → { notes }; POST … { content }
 *   → { newNoteId }; PUT and DELETE /locations/:id/journal/:noteId.
 *
 * Who uses it: features/plant-list (NotesSection, NoteBox, the photo
 * dialogs' note, PlantDoctor's "save as a note", plantNotesCache), the
 * plant form, Today's garden chores, and api/locations (the Note type).
 * Caching: ['notes', subject.kind, subject.id] (NotesSection), and the
 * plants list itself carries each plant's notes, which plantNotesCache
 * updates in place under ['plants'] after a write. Auth: the signed-in
 * token, in the garden being tended; the server refuses a plant that is
 * not that garden's.
 *
 * @module api/notes
 */

import { apiClient } from './apiClient';
import type { Subject } from './subject';

/** One entry in a plant's journal. */
export interface Note {
  /** The note's id. */
  note_id: number;
  /** Its words, up to the server's limits.NOTE_MAX_LENGTH (lib/growSettings). */
  content: string;
  /** MySQL datetime, most recent first as the procedure returns them. */
  timestamp: string;
  /** The Plant Doctor checkup this note was saved from; null or absent for any other note. */
  checkup_id?: number | null;
  /** True when the gardener hid this checkup note from their public pages; only a checkup note has the switch. */
  hidden_from_public?: boolean;
  /** True when a photo carries this note as its caption (the server says so): it is the photo's words, not a journal entry. */
  belongsToPhoto?: boolean;
}

/**
 * The notes of the journal: every note the server does not mark as a photo's
 * caption. The one place that decides it, for the count by Notes and for the
 * list under it.
 *
 * @param notes - A plant's or a bed's notes as the server sent them.
 * @returns The notes that are not a photo's caption, in the order given.
 */
export function journalNotes(notes: readonly Note[] | undefined | null): Note[] {
  return (notes ?? []).filter((note) => !note.belongsToPhoto);
}

/**
 * Every note on one plant, newest first.
 *
 * @param plantObjId - Plant to read. The server refuses one the caller does
 *   not own, so there is no need to filter here.
 * @returns Its notes; [] if the answer is not a list.
 */
export async function getNotes(plantObjId: number): Promise<Note[]> {
  const response = await apiClient.get<Note[]>(`/notes/${plantObjId}`);
  return Array.isArray(response.data) ? response.data : [];
}

/**
 * Writes a note against a plant, dated now by the database.
 *
 * @param plantObjId - Plant the note is about.
 * @param content - Up to the server's limits.NOTE_MAX_LENGTH characters.
 * @param checkupId - The Plant Doctor checkup the note is saved from, so it can show on the plant's public pages.
 * @returns The new note's id, or null if the server did not report one.
 */
export async function createNote(plantObjId: number, content: string, checkupId?: number | null): Promise<number | null> {
  const response = await apiClient.post<Array<{ newNoteId?: number }>>(
    `/notes/${plantObjId}`,
    checkupId ? { content, checkupId } : { content }
  );
  // The procedure answers with a one-row set carrying the new id. A caller that
  // only wants the note written can ignore it; the photo upload needs it, so
  // that the picture can point at the words rather than merely share a date.
  return response.data?.[0]?.newNoteId ?? null;
}

/**
 * Rewrites an existing note.
 *
 * The endpoint is keyed by note but still wants the plant in the body, which
 * is how the procedure checks ownership.
 *
 * @param noteId - The note.
 * @param plantObjId - The plant it is on.
 * @param content - The new words.
 */
export async function updateNote(noteId: number, plantObjId: number, content: string): Promise<void> {
  await apiClient.put(`/notes/${noteId}`, { plantObjId, content });
}

/**
 * Removes a note for good. There is no archive for these.
 *
 * @param plantObjId - The plant it is on.
 * @param noteId - The note.
 */
export async function deleteNote(plantObjId: number, noteId: number): Promise<void> {
  await apiClient.delete(`/notes/${plantObjId}/${noteId}`);
}

/**
 * Hides a saved Plant Doctor checkup note from the gardener's public pages, or
 * shows it again. Only the garden's owner may (a member tending it is refused).
 *
 * @param plantObjId - The plant the note is on.
 * @param noteId - The note, one saved from a checkup.
 * @param hidden - True to hide it, false to show it again.
 * @returns Whether the note is hidden now.
 * @throws The request's error (OWNER_ONLY for a member).
 */
export async function setNoteHiddenFromPublic(plantObjId: number, noteId: number, hidden: boolean): Promise<boolean> {
  const response = await apiClient.patch<{ hidden_from_public?: boolean }>(`/notes/${plantObjId}/${noteId}/public`, { hidden });
  return Boolean(response.data?.hidden_from_public);
}

/*
 * The same note actions for either subject. A location's notes live in
 * /locations/:id/journal and come back in the same shape.
 */

/**
 * Every note on a plant or a location, newest first.
 *
 * @param subject - Whose notes.
 */
export async function getNotesFor(subject: Subject): Promise<Note[]> {
  if (subject.kind === 'plant') return getNotes(subject.id);
  const response = await apiClient.get<{ notes: Note[] }>(`/locations/${subject.id}/journal`);
  return response.data.notes ?? [];
}

/**
 * Writes a note on a plant or a location.
 *
 * @param subject - Whose note.
 * @param content - The words.
 * @returns The new note's id, or null if the server did not report one.
 */
export async function createNoteFor(subject: Subject, content: string): Promise<number | null> {
  if (subject.kind === 'plant') return createNote(subject.id, content);
  const response = await apiClient.post<{ newNoteId?: number }>(`/locations/${subject.id}/journal`, { content });
  return response.data.newNoteId ?? null;
}

/**
 * Rewrites a plant's or a location's note.
 *
 * @param subject - Whose note.
 * @param noteId - The note.
 * @param content - The new words.
 */
export async function updateNoteFor(subject: Subject, noteId: number, content: string): Promise<void> {
  if (subject.kind === 'plant') return updateNote(noteId, subject.id, content);
  await apiClient.put(`/locations/${subject.id}/journal/${noteId}`, { content });
}

/**
 * Removes a plant's or a location's note for good.
 *
 * @param subject - Whose note.
 * @param noteId - The note.
 */
export async function deleteNoteFor(subject: Subject, noteId: number): Promise<void> {
  if (subject.kind === 'plant') return deleteNote(subject.id, noteId);
  await apiClient.delete(`/locations/${subject.id}/journal/${noteId}`);
}
